import { Box, Paper, styled } from "@mui/material";
import * as React from "react";
import { Rnd } from "react-rnd";
import { useGameRoom, useGameContext } from "../../context/GameContext";
import { TwilioProvider, useTwilioContext } from "../../context/TwilioProvider";
import useParticipants from "../hooks/useParticipants";
import { ParticipantContainer } from "./ParticipantContainer";
import { AdminContainer } from "./AdminContainer";
import { useAuthUser } from "context/GameAuthContext";

const GameCallWindow = () => {
	const { room } = useGameContext();
	const { token } = useAuthUser();

	return (
		<TwilioProvider
			token={token}
			acquireLocalTracks={true}
			roomId={room!.roomId}
			isEnabled={(room as any)?.users?.length > 1 || (room as any)?.twilioForSinglePlayer || false}
		>
			<CallContainer />
		</TwilioProvider>
	);
};

const videoWidth = 100;
const videoHeight = 194;

const CallPaper = styled(Paper)({
	boxShadow: "0px 15px 40px #77777724",
	borderRadius: 5,
	padding: 8,
	height: "100%",
	width: "100%",
});

const ParticipantGrid = styled("div")({
	width: "100%",
	height: "100%",
});

const CallContainer = () => {
	const { room } = useTwilioContext();
	const gameRoom = useGameRoom();
	const allParticipants = useParticipants();
	const localParticipant = room!.localParticipant;

	if (!gameRoom) {
		return null;
	}

	const participants = allParticipants.filter((participant) => {
		const userId = participant.identity.split("@#")[0];
		// Only show particpants who are actually official player of the game room
		return gameRoom?.users.some((user) => userId === user._id);
	});

	const spectatorParticipants = allParticipants.filter((participant) => {
		return !participants.some(({ identity }) => participant.identity === identity);
	});

	const participantCount = participants.length + 1; // Remote + local

	const minWidth = participantCount <= 2 ? videoWidth : participantCount <= 4 ? videoWidth * 2 : videoWidth * 3;
	const minHeight = participantCount === 1 ? videoHeight : videoHeight * 2;

	const twoParticipants = () => {
		return (
            <Box height="100%" display="grid" gridTemplateRows="repeat(2, 1fr)" gap="8px">
				<ParticipantGrid>
					<ParticipantContainer participant={localParticipant} isLocalParticipant={true} />
				</ParticipantGrid>

				{participants.map((participant) => (
					<ParticipantGrid key={participant.identity}>
						<ParticipantContainer participant={participant} isLocalParticipant={false} />
					</ParticipantGrid>
				))}
			</Box>
        );
	};
	const threeParticipants = () => {
		return (
            <Box
				height="100%"
				display="grid"
				gridTemplateRows="repeat(2, 1fr)"
				gridTemplateAreas={`"a c" 
			"b c"`}
				gap="8px"
			>
				<ParticipantGrid style={{ gridArea: "a" }}>
					<ParticipantContainer participant={participants[0]} isLocalParticipant={false} />
				</ParticipantGrid>
				<ParticipantGrid style={{ gridArea: "b" }}>
					<ParticipantContainer participant={participants[1]} isLocalParticipant={false} />
				</ParticipantGrid>
				<ParticipantGrid style={{ gridArea: "c" }}>
					<ParticipantContainer participant={localParticipant} isLocalParticipant={true} />
				</ParticipantGrid>
			</Box>
        );
	};
	const fourParticipants = () => {
		return (
            <Box
				height="100%"
				display="grid"
				gridTemplateRows="repeat(2, 1fr)"
				gridTemplateColumns="repeat(2, 1fr)"
				gap="8px"
			>
				<ParticipantGrid>
					<ParticipantContainer participant={localParticipant} isLocalParticipant={true} />
				</ParticipantGrid>
				{participants.map((participant) => (
					<ParticipantGrid key={participant.identity}>
						<ParticipantContainer participant={participant} isLocalParticipant={false} />
					</ParticipantGrid>
				))}
			</Box>
        );
	};
	const fiveParticipants = () => {
		return (
            <Box
				height="100%"
				display="grid"
				gridTemplateRows="repeat(2, 1fr)"
				gridTemplateAreas={`"a b e" 
			"c d e"`}
				gap="8px"
			>
				<ParticipantGrid style={{ gridArea: "a" }}>
					<ParticipantContainer participant={participants[0]} isLocalParticipant={false} />
				</ParticipantGrid>
				<ParticipantGrid style={{ gridArea: "b" }}>
					<ParticipantContainer participant={participants[1]} isLocalParticipant={false} />
				</ParticipantGrid>
				<ParticipantGrid style={{ gridArea: "c" }}>
					<ParticipantContainer participant={participants[2]} isLocalParticipant={false} />
				</ParticipantGrid>
				<ParticipantGrid style={{ gridArea: "d" }}>
					<ParticipantContainer participant={participants[3]} isLocalParticipant={false} />
				</ParticipantGrid>
				<ParticipantGrid style={{ gridArea: "e" }}>
					<ParticipantContainer participant={localParticipant} isLocalParticipant={true} />
				</ParticipantGrid>
			</Box>
        );
	};

	return (
		<Rnd
			bounds="window"
			default={{ y: 100, x: 100, height: 120, width: 200 }}
			minWidth={minWidth}
			minHeight={minHeight}
			enableResizing={true}
			style={{ zIndex: 99999 }} // Make sure it always stays on top of everything
		>
			<CallPaper>
				{participantCount === 1 && (
					<ParticipantGrid>
						<ParticipantContainer participant={localParticipant} isLocalParticipant={true} />
					</ParticipantGrid>
				)}
				{participantCount === 2 && twoParticipants()}
				{participantCount === 3 && threeParticipants()}
				{participantCount === 4 && fourParticipants()}
				{participantCount === 5 && fiveParticipants()}
				{spectatorParticipants.map((adminParticipant) => (
					<AdminContainer key={adminParticipant.identity} participant={adminParticipant}></AdminContainer>
				))}
			</CallPaper>
		</Rnd>
	);
};

export default GameCallWindow;
